import { handleListBoxListeners } from '@tesla/design-system';
import { iconCheckSmall } from '@tesla/design-system-icons';
import cx from 'classnames';
import React, { useEffect, useRef } from 'react';
import { useNormalizedOptions } from '../hooks/use-normalized-options.js';
import { Icon, useId } from '../index.js';
import { toOption, toValue } from './utils.js';
/**
 * ListBox
 * - Either a styled replacement for a select element's dropdown
 * - Or a generic widget for selecting one option from several
 *
 * Reference:
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/listbox_role#States_and_Properties
 * https://www.w3.org/TR/wai-aria-practices/examples/listbox/listbox-collapsible.html
 */
export const ListBox = ({ activeDescendant, 'aria-label': ariaLabel = 'Select an option', containerRef, id, inputRef, listboxRef, maxRenderedOptions = 500, onActiveDescendantChange, onClear, onOptionSelect, options: rawOptions, selected, ...rest }) => {
    const options = useNormalizedOptions(rawOptions);
    const fallbackRef = useRef(null);
    const ref = listboxRef || fallbackRef;
    const uid = useId();
    const htmlId = id || uid;
    const multiple = Array.isArray(selected);
    /**
     * Vanilla Listeners:
     * - Manage all the various event listeners in vanilla :)
     */
    useEffect(() => {
        const handleOptionSelect = (option) => {
            const matchingOption = options.find((item) => {
                return toValue(option) === toValue(item);
            });
            if (!(matchingOption === null || matchingOption === void 0 ? void 0 : matchingOption.disabled)) {
                onOptionSelect === null || onOptionSelect === void 0 ? void 0 : onOptionSelect(matchingOption || option);
            }
        };
        const cleanup = handleListBoxListeners({
            container: (containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) || undefined,
            input: inputRef === null || inputRef === void 0 ? void 0 : inputRef.current,
            listbox: ref.current,
            // @ts-expect-error - temp fix to resolve build errors, revisit this ts diff
            onActiveDescendantChange,
            onClear,
            // @ts-expect-error - temp fix to resolve build errors, revisit this ts diff
            onOptionSelect: handleOptionSelect,
        });
        return cleanup;
    }, [
        options,
        containerRef,
        ref,
        inputRef,
        activeDescendant,
        onActiveDescendantChange,
        onOptionSelect,
        onClear,
    ]);
    // Scroll the active element into view if it's not already
    useEffect(() => {
        if (activeDescendant && ref.current) {
            const activeElement = document.getElementById(activeDescendant);
            activeElement === null || activeElement === void 0 ? void 0 : activeElement.scrollIntoView({ block: 'nearest' });
        }
    }, [ref, activeDescendant]);
    return (React.createElement("ul", { "aria-activedescendant": (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) ? undefined : activeDescendant, "aria-label": ariaLabel, "aria-multiselectable": multiple, className: "tds-listbox", id: htmlId, ref: ref, role: "listbox", 
        // Allow focusing on the listbox if no input is provided
        tabIndex: !(inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) ? 0 : -1, ...rest }, (maxRenderedOptions !== 'unlimited' ? options.slice(0, maxRenderedOptions) : options).map((rawOption) => {
        var _a;
        // Build option object if string
        const option = toOption(rawOption);
        const optionId = (_a = option === null || option === void 0 ? void 0 : option.id) !== null && _a !== void 0 ? _a : `${htmlId}-${toValue(option)}`;
        // ensure that custom ids generate unique keys - a bit belt & suspenders, but
        // better safe than throwing unique key errors
        const optionKey = (option === null || option === void 0 ? void 0 : option.id) ? `${htmlId}-${optionId}` : optionId;
        const optionSelected = multiple
            ? selected === null || selected === void 0 ? void 0 : selected.some((opt) => toValue(opt) === toValue(option))
            : toValue(selected) === toValue(option);
        return (React.createElement("li", { "aria-disabled": option.disabled, "aria-selected": optionSelected || undefined, className: cx('tds-listbox-option', {
                'tds--selected': optionSelected,
                'tds--disabled': option.disabled,
                // Per ARIA 1.2, Use aria-active-descendant to visually indicate focus on the active element
                // while keeping keyboard/DOM focus on the input
                'tds--focus': activeDescendant === optionId,
            }), "data-tds-label": option.label, "data-tds-value": toValue(option), id: optionId, key: optionKey, role: "option" },
            multiple && (React.createElement("span", { className: "tds-multiselect-box" },
                React.createElement(Icon, { className: "tds-multiselect-icon", data: iconCheckSmall }))),
            React.createElement("span", null, option.fullLabel || option.label),
            !multiple && React.createElement(Icon, { className: "tds-singleselect-icon", data: iconCheckSmall })));
    })));
};
